import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/nail-technician-real.jpg";

export const nailTechnicians: Persona = {
  slug: "nail-technicians",
  profession: { hu: "körmös", en: "nail technician" },
  professionPlural: { hu: "körmösök", en: "nail technicians" },
  professionGenitive: { hu: "körmösként", en: "as a nail technician" },
  heroH1: {
    hu: "Tele naptár, hűséges vendégek. Indulj 10 perc alatt.",
    en: "Full calendar, loyal clients. Launch in 10 minutes.",
  },
  heroSub: {
    hu: "AI weboldal, online foglalás, bérletek és visszatérési emlékeztetők: egy platformon szóló körmösöknek és szalonoknak.",
    en: "AI website, online booking, passes and rebooking reminders: one platform for solo nail techs and salons.",
  },
  heroImage,
  heroImageAlt: "Nail technician applying a gel-lac treatment",
  chatExamplePrompt: {
    hu: "Tegyél be egy gel-lac kezelést Évának szombat 15-re",
    en: "Book a gel-lac for Éva on Saturday at 3 PM",
  },
  chatExampleResponse: {
    hu: "Bejegyeztem szombat 15:00, 90 perc, gel-lac kezelés. Évának visszaigazolást küldtem.",
    en: "Booked Saturday 3 PM, 90 minutes, gel-lac treatment. Confirmation sent to Éva.",
  },
  accentColor: "rose",

  defaultMode: "both-equal",
  teamSize: { min: 1, max: 8 },
  soloLabel: { hu: "Szóló körmös", en: "Solo nail tech" },
  teamLabel: {
    hu: "Szalon több körmössel",
    en: "Salon with multiple nail techs",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Megnyitod a napot: 8 időpont, 1 új vendég. Az AI mindenkit visszaigazolt.",
        en: "Open the day: 8 appointments, 1 new guest. AI confirmed everyone.",
      },
    },
    {
      time: "11:00",
      what: {
        hu: "Új lead Instagramról. Az AI válaszolt az árakról, lefoglalta a próba gel-lac-et.",
        en: "New lead from Instagram. AI answered pricing, booked the trial gel-lac.",
      },
    },
    {
      time: "14:00",
      what: {
        hu: "Egy vendég lemond. Az AI 60 mp alatt felajánlja a várólista első emberének, telik a slot.",
        en: "A guest cancels. AI offers the slot to the first person on the waitlist within 60 seconds: filled.",
      },
    },
    {
      time: "17:00",
      what: {
        hu: "Az AI észrevette, hogy Anna 4 hete nem volt. Email ment 10% kedvezménnyel.",
        en: "AI noticed Anna hasn't been in 4 weeks. Email went out with 10% off.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Napzárás: 9 vendég, 76 000 Ft, 2 új visszatérő. Számlák kiállítva.",
        en: "End of day: 9 guests, 76,000 HUF, 2 new returners. Invoices issued.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Szalon dashboard: 3 körmösöd 18 időpontot ad ma. Mindenki saját nézettel, te mindent látsz.",
        en: "Salon dashboard: your 3 nail techs have 18 appointments today. Each has their own view, you see everything.",
      },
    },
    {
      time: "10:30",
      what: {
        hu: "Vivi beteg. Az AI áthelyezi 4 vendégét Annához, mindenkit értesít.",
        en: "Vivi is sick. AI moves her 4 guests to Anna, notifies everyone.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új körmös, Bea érkezik. Felveszed a Bespire-be, 5 percen belül publikálódik a profilja.",
        en: "New nail tech Bea joins. You add her to Bespire, profile live in 5 minutes.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI mutatja: a műköröm-építés +28% bevétel a héten. Javasolja az ár-emelést.",
        en: "AI shows: nail extensions are +28% revenue this week. Suggests a price increase.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Hétvégi 'menyasszony-csomag' akció telik. AI várólistára teszi a túljelentkezőket.",
        en: "Weekend 'bridal package' promo fills up. AI puts overflow on the waitlist.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel körmösönként, jutalék-elszámolás kész, számlák kiállítva.",
        en: "Revenue per nail tech, commission ready, invoices issued.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: {
        hu: "Naptár és vendég: egy helyen",
        en: "Calendar and clients in one place",
      },
      body: {
        hu: "Nem kell Excel, nem kell WhatsApp scroll. Mindent egy fiókban kezelsz.",
        en: "No Excel, no WhatsApp scrolling. Manage everything in one account.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Lead-űrlap + automata válasz",
        en: "Lead form + auto-response",
      },
      body: {
        hu: "Új érdeklődő kitölti a foglalási űrlapot, automatikus visszaigazoló email és foglalási link megy ki: neked már csak a kezelést kell elvégezni.",
        en: "New leads fill out a booking form, auto-confirmation email and booking link go out: you just do the treatment.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "Automata emlékeztetők", en: "Automatic reminders" },
      body: {
        hu: "Email és SMS 24 órával előtte: drasztikusan csökken a no-show arány.",
        en: "Email and SMS 24 hours ahead: no-show rate drops drastically.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan körmös, egy ár",
        en: "Unlimited nail techs, one price",
      },
      body: {
        hu: "1 vagy 8 fő: ugyanaz a havidíj. Mindenki saját naptárral és vendégkörrel.",
        en: "1 or 8 people: same monthly fee. Each with their own calendar and clients.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Tulaj, recepciós, körmös: mindenki csak azt látja, amihez joga van.",
        en: "Owner, receptionist, nail tech: everyone sees only what they're allowed to.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a vendégeket", en: "AI assigns guests" },
      body: {
        hu: "Új lead: az AI specialitás (gel-lac, műköröm, nail art) alapján a megfelelő körmöshöz osztja.",
        en: "New lead: AI assigns by specialty (gel-lac, extensions, nail art) to the right nail tech.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Automatikus jutalék-elszámolás",
        en: "Automatic commission settlement",
      },
      body: {
        hu: "Ki mennyit dolgozott, milyen bevételt hozott: exportálható, Számlázz.hu kompatibilis.",
        en: "Who worked how much, what revenue: exportable, Számlázz.hu compatible.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: { hu: "Visszatérő foglalások", en: "Recurring bookings" },
      body: {
        hu: "Egy klikkel ismétlődő időpont, 3-4 hetente automata emlékeztető SMS/email/WhatsApp-en a töltésre.",
        en: "One-click recurring slots, auto reminders every 3:4 weeks via SMS/email/WhatsApp for refills.",
      },
    },
    {
      icon: "CreditCard",
      title: { hu: "Bérletek és csomagok", en: "Passes and packages" },
      body: {
        hu: "5/10 alkalmas bérletek, kezelés-csomagok: automata bérletvonás, lejárati emlékeztető.",
        en: "5/10-session passes, treatment bundles: auto-deduction, expiry reminders.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "No-show védelem", en: "No-show protection" },
      body: {
        hu: "Foglaláskor előleg, 24 órás emlékeztető, lemondási szabályok: kevesebb üres slot.",
        en: "Deposit at booking, 24-hour reminders, cancellation rules: fewer empty slots.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Ügyfél-visszahívó kampány", en: "Win-back campaigns" },
      body: {
        hu: "Ha egy vendég 6+ hete nem volt, automata email vagy WhatsApp emlékeztető kedvezménnyel: te állítod be, a rendszer kiküldi.",
        en: "If a guest hasn't been in 6+ weeks, an automatic email or WhatsApp with a discount: you configure, the system sends.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "Több körmös dolgozik nálunk: bonyolult lesz?",
        en: "We have multiple nail techs: will it be complicated?",
      },
      answer: {
        hu: "Nem. Minden körmösnek saját naptára, vendéglistája, jutalék-statisztikája van. Te tulajdonosként mindent látsz, ők csak a sajátjukat. Korlátlan körmöst vehetsz fel, az ár nem változik (1 vagy 8 fő: ugyanaz a 10 000 Ft + ÁFA / hó).",
        en: "No. Each nail tech has their own calendar, clients and commission. As owner you see everything, they only see theirs. Add unlimited nail techs, the price doesn't change (1 or 8, same 10,000 HUF + VAT / month).",
      },
    },
    {
      question: {
        hu: "A vendégeim telefonon foglalnak",
        en: "My clients book by phone",
      },
      answer: {
        hu: "Maradhat a telefon: te a Bespire-ben jegyzed be 10 mp alatt. De a 24/7 online foglalás odavonzza a fiatalabb vendégkört is, akiknek 22:00-kor jut eszükbe lefoglalni a hétvégi tölést.",
        en: "Phone bookings stay: you enter them in Bespire in 10 seconds. But 24/7 online booking also attracts younger clients who remember at 10 PM to book a weekend refill.",
      },
    },
    {
      question: {
        hu: "Mi van, ha a vendég nem jelenik meg?",
        en: "What if a guest doesn't show?",
      },
      answer: {
        hu: "Beállíthatsz előleg-fizetést a foglaláskor, vagy lemondási szabályt (pl. 12 órán belüli lemondás = előleg elvész). Az AI 24 órával előtte automatikusan emlékeztet.",
        en: "Set a deposit at booking, or a cancellation rule (e.g. cancel within 12 hours = lose deposit). AI reminds 24 hours ahead automatically.",
      },
    },
    {
      question: {
        hu: "El tudom hozni a régi vendég-listámat?",
        en: "Can I bring my old client list?",
      },
      answer: {
        hu: "Igen. CSV/Excel importtal, vagy a Beüzemelés szolgáltatás keretében átvesszük a teljes vendégkört, korábbi időpontokat, kedvenc színeket.",
        en: "Yes. Via CSV/Excel import, or our Setup service migrates the full client base, past appointments and favorite colors.",
      },
    },
  ],

  monetization: [
    {
      icon: "Heart",
      title: { hu: "Gel-lac kezelések", en: "Gel-lac treatments" },
      examples: [
        { hu: "Klasszikus gel-lac", en: "Classic gel-lac" },
        { hu: "French gel-lac", en: "French gel-lac" },
        { hu: "Töltés (3-4 hét)", en: "Refill (3:4 weeks)" },
      ],
      priceHint: {
        hu: "Példa: 80 kezelés × 9 500 Ft = 760 000 Ft / hó (gel-lac 7:12e Ft)",
        en: "Example: 80 treatments × 9,500 HUF = 760,000 HUF / month (gel-lac 7:12k HUF)",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Műköröm építés", en: "Nail extensions" },
      examples: [
        { hu: "Akrilköröm építés", en: "Acrylic extensions" },
        { hu: "Gel-építés", en: "Gel extensions" },
        { hu: "Hosszabbítás", en: "Length extension" },
      ],
      priceHint: {
        hu: "Példa: 25 építés × 18 000 Ft = 450 000 Ft / hó (építés 15:22e Ft)",
        en: "Example: 25 sets × 18,000 HUF = 450,000 HUF / month (15:22k HUF)",
      },
    },
    {
      icon: "Smile",
      title: { hu: "Nail art és különleges", en: "Nail art and specialty" },
      examples: [
        { hu: "Festett mintás köröm", en: "Hand-painted designs" },
        { hu: "Strasszozás", en: "Rhinestones" },
        { hu: "3D dekoráció", en: "3D decoration" },
      ],
      priceHint: {
        hu: "Példa: 18 nail art × 14 000 Ft = 252 000 Ft / hó",
        en: "Example: 18 nail art × 14,000 HUF = 252,000 HUF / month",
      },
    },
    {
      icon: "Package",
      title: { hu: "Pedikűr és kiegészítő", en: "Pedicure and add-ons" },
      examples: [
        { hu: "Klasszikus pedikűr", en: "Classic pedicure" },
        { hu: "Gel pedi", en: "Gel pedi" },
        { hu: "Otthoni ápolási kit", en: "Home-care kit" },
      ],
      priceHint: {
        hu: "Példa: 22 pedi × 8 500 Ft = 187 000 Ft / hó",
        en: "Example: 22 pedis × 8,500 HUF = 187,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Bérletek és tagságok", en: "Passes and memberships" },
      examples: [
        { hu: "6 alkalmas gel-lac bérlet", en: "6-session gel-lac pass" },
        { hu: "Havi VIP (gel-lac + pedi)", en: "Monthly VIP (gel-lac + pedi)" },
        { hu: "Éves szépség-kártya", en: "Annual beauty card" },
      ],
      priceHint: {
        hu: "Szalon-szint: 3 körmös × 30 bérletes vendég × 18 000 Ft = 1 620 000 Ft / hó potenciál",
        en: "Salon level: 3 nail techs × 30 pass holders × 18,000 HUF = 1,620,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+44%",
      label: { hu: "havi vendég-szám", en: "monthly guest count" },
    },
    {
      value: "-78%",
      label: { hu: "elmaradt időpont", en: "missed appointments" },
    },
    {
      value: "4.9★",
      label: { hu: "vendég-vélemény átlag", en: "guest review average" },
    },
    {
      value: "10 perc",
      label: { hu: "új vendég foglalása", en: "new guest booking" },
    },
  ],
  testimonial: {
    quote: {
      hu: "A vendégek mindig túl későn jelezték, hogy nem jönnek. Most a Bespire 24 órával előtte emlékeztet: és aki mégis lemond, helyette az AI azonnal értesít valakit a várólistáról.",
      en: "Guests always told me too late they couldn't come. Now Bespire reminds them 24 hours ahead: and if someone still cancels, AI immediately calls someone from the waitlist.",
    },
    name: "Kis Vivien",
    role: { hu: "Műkörmös", en: "Nail technician" },
    city: { hu: "Kecskemét", en: "Kecskemét" },
    avatarInitials: "KV",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a szolgáltatásokat",
        en: "Tell us your services in chat",
      },
      body: {
        hu: "Gel-lac, építés, japán manikűr, pedikűr: az AI árazza és időt rendel.",
        en: "Gel-lac, extensions, Japanese manicure, pedi: AI prices and assigns durations.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a szalon oldalt",
        en: "AI builds the salon site",
      },
      body: {
        hu: "Vendég-kartonok, bérletek, fotó-galéria előző munkákról.",
        en: "Client cards, passes, photo gallery of past work.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk a törzs-vendégeket",
        en: "We import your regulars",
      },
      body: {
        hu: "Korábbi színek, preferenciák: egy kartonra.",
        en: "Past colors, preferences: onto one card.",
      },
    },
    {
      step: 4,
      title: { hu: "Élő, online foglalás", en: "Live, online booking" },
      body: {
        hu: "Instagramról egy klikkre, várólista, automata emlékeztető.",
        en: "One click from Instagram, waitlist, auto-reminders.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "Meta",
    "WhatsApp Business",
  ],
  breadcrumbLabel: {
    hu: "Körmösök és nail szalonok",
    en: "Nail techs & nail salons",
  },
  similarPersonas: [
    "beauticians-beauty-salons",
    "hairdressers-barbers",
    "massage-therapists",
  ],
};
